/**
 * Audit feature — query hooks.
 *
 * Read-only. Audit records cannot be mutated from the UI — they're
 * the compliance record. Export and archival are separate
 * back-office operations.
 *
 * Backed by internal/ops/audit.AdminHandler:
 *   - Tenant surface: /api/v1/tenants/{tenant_id}/audit-events
 *   - Platform surface: /api/v1/audit-events
 */

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import {
  auditListResponseSchema,
  auditVerifyResultSchema,
  platformAuditListResponseSchema,
  windowsAuthenticationsResponseSchema,
  type AuditListResponse,
  type AuditListSearch,
  type AuditVerifyResult,
  type WindowsAuthenticationsResponse,
} from "./schemas";

function tenantBase(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/audit-events`;
}

/**
 * Convert the URL-driven search params into the concrete query
 * string the backend expects. When a `preset` is set it wins over
 * any explicit from/to (the schema already encodes the mutual
 * exclusion, but we also resolve it here so the wire call is
 * unambiguous even if a hand-crafted URL breaks the contract).
 */
function buildSearchParams(search: AuditListSearch) {
  const range = resolvePresetRange(search.preset);
  // Mapping rules:
  //  - `q` is the broad substring search the backend runs across
  //    actor email / event name / target id.
  //  - `actor` is the free-text fragment box on the UI; the
  //    backend has no matching "actor fragment" filter, so we
  //    route it into `actor_email` (email-fragment search — the
  //    only first-class name-like filter it exposes). Operators
  //    who want an exact-id pivot use `actor_id` instead.
  //  - `actor_id` + `resource_id` are the exact-UUID pivots used
  //    by feature-page deep-links (Automation account detail "View
  //    audit" etc.). Backend 400s on malformed UUIDs; our schema
  //    validates UUID shape upfront.
  return {
    q: search.q,
    event: search.event,
    event_prefix: search.event_prefix,
    actor_email: search.actor,
    actor_id: search.actor_id,
    resource_id: search.resource_id,
    subject_id: search.subject_id,
    subject_type: search.subject_type,
    resource_type: search.resource_type,
    ip_address: search.ip,
    session_id: search.session,
    from: range?.from ?? search.from,
    to: range?.to ?? search.to,
    result: search.result,
    offset: search.offset,
    limit: search.limit,
  };
}

/**
 * Resolve a preset string into an ISO range. Computed at request
 * time (not when the URL is parsed) so every refresh uses a fresh
 * "now" — "last 15 minutes" always means the 15 minutes leading
 * up to the query, not the 15 minutes before the tab was opened.
 */
function resolvePresetRange(
  preset: AuditListSearch["preset"],
): { from: string; to: string } | null {
  if (!preset) return null;
  const now = new Date();
  const to = now.toISOString();
  const windowMs: Record<NonNullable<AuditListSearch["preset"]>, number> = {
    "15m": 15 * 60 * 1_000,
    "1h": 60 * 60 * 1_000,
    "24h": 24 * 60 * 60 * 1_000,
    "7d": 7 * 24 * 60 * 60 * 1_000,
  };
  const from = new Date(now.getTime() - windowMs[preset]).toISOString();
  return { from, to };
}

export async function fetchTenantAudit(
  tenantID: string,
  search: AuditListSearch,
): Promise<AuditListResponse> {
  const raw = await apiFetch<unknown>(tenantBase(tenantID), {
    searchParams: buildSearchParams(search),
  });
  return auditListResponseSchema.parse(raw);
}

export function useTenantAuditQuery(tenantID: string, search: AuditListSearch, options: { enabled?: boolean } = {}) {
  return useQuery<AuditListResponse, ApiError>({
    queryKey: queryKeys.audit.tenantEvents(tenantID, search),
    queryFn: () => fetchTenantAudit(tenantID, search),
    enabled: tenantID.length > 0 && (options.enabled ?? true),
    staleTime: 60_000,
    // Paging changes the query key (offset is part of `search`); keep the prior
    // page visible (dimmed) instead of flashing a full loading state between
    // pages. Benefits every consumer — the main audit page and the per-agent tab.
    placeholderData: keepPreviousData,
  });
}

export async function fetchWindowsAuthentications(
  tenantID: string,
  search: AuditListSearch,
): Promise<WindowsAuthenticationsResponse> {
  const range = resolvePresetRange(search.preset);
  const raw = await apiFetch<unknown>(
    `${tenantBase(tenantID)}/windows-authentication`,
    {
      searchParams: {
        q: search.q,
        user_id: search.actor_id,
        workstation_id: search.resource_id,
        origin: search.origin,
        outcome: search.outcome,
        from: range?.from ?? search.from,
        to: range?.to ?? search.to,
        offset: search.offset,
        limit: search.limit,
      },
    },
  );
  return windowsAuthenticationsResponseSchema.parse(raw);
}

export function useWindowsAuthenticationsQuery(
  tenantID: string,
  search: AuditListSearch,
  options: { enabled?: boolean } = {},
) {
  return useQuery<WindowsAuthenticationsResponse, ApiError>({
    queryKey: queryKeys.audit.windowsAuthentications(tenantID, search),
    queryFn: () => fetchWindowsAuthentications(tenantID, search),
    enabled: tenantID.length > 0 && (options.enabled ?? true),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

export function windowsAuthenticationsExportURL(
  tenantID: string,
  search: AuditListSearch,
  format: "csv" | "ndjson",
): string {
  const range = resolvePresetRange(search.preset);
  const params = new URLSearchParams();
  if (search.actor_id) params.set("user_id", search.actor_id);
  if (search.q) params.set("q", search.q);
  if (search.resource_id) params.set("workstation_id", search.resource_id);
  // The export carries the SAME population narrowing as the list. An export that silently widened
  // to rows the operator had filtered out would hand a compliance reviewer machine claims they
  // never asked to see, in a file that no longer matches the screen it came from.
  if (search.origin) params.set("origin", search.origin);
  if (search.outcome) params.set("outcome", search.outcome);
  if (range?.from ?? search.from) params.set("from", range?.from ?? search.from ?? "");
  if (range?.to ?? search.to) params.set("to", range?.to ?? search.to ?? "");
  params.set("format", format);
  return `${tenantBase(tenantID)}/windows-authentication/export?${params.toString()}`;
}

// ── Platform audit ────────────────────────────────────────────────────────

export async function fetchPlatformAudit(
  search: AuditListSearch,
): Promise<AuditListResponse> {
  const raw = await apiFetch<unknown>("/api/v1/audit-events", {
    searchParams: buildSearchParams(search),
  });
  const page = platformAuditListResponseSchema.parse(raw);
  // A platform row names the tenant it touched rather than an owning tenant; the shared row type
  // spells that absence as tenant_id: null.
  return { ...page, data: page.data.map((row) => ({ ...row, tenant_id: null })) };
}

export function usePlatformAuditQuery(
  search: AuditListSearch,
  options: { enabled?: boolean } = {},
) {
  return useQuery<AuditListResponse, ApiError>({
    queryKey: queryKeys.audit.platformEvents(search),
    queryFn: () => fetchPlatformAudit(search),
    staleTime: 60_000,
    enabled: options.enabled ?? true,
    // Keep the prior page visible (dimmed) while the next offset loads,
    // matching the tenant surface — no full-page spinner between pages.
    placeholderData: keepPreviousData,
  });
}

// ── Integrity verify ──────────────────────────────────────────────────────

export type { AuditVerifyResult };

/**
 * Recompute the HMAC for one audit row and return the verdict.
 * The backend never returns the integrity_hash itself — only
 * whether it matches what the catalog-keyed signer would produce
 * for the current row. See backend admin_handler.VerifyTenant.
 */
export async function verifyTenantAuditEvent(
  tenantID: string,
  eventID: string,
): Promise<AuditVerifyResult> {
  const raw = await apiFetch<unknown>(
    `${tenantBase(tenantID)}/${encodeURIComponent(eventID)}/verify`,
    { method: "POST" },
  );
  return auditVerifyResultSchema.parse(raw);
}

export async function verifyPlatformAuditEvent(
  eventID: string,
): Promise<AuditVerifyResult> {
  const raw = await apiFetch<unknown>(
    `/api/v1/audit-events/${encodeURIComponent(eventID)}/verify`,
    { method: "POST" },
  );
  return auditVerifyResultSchema.parse(raw);
}

// ── Export ────────────────────────────────────────────────────────────────

/**
 * Build an export URL (caller triggers the download by navigating
 * to it, so the browser's native streaming + download dialog is
 * used). We intentionally do NOT fetch + Blob the response because
 * an export can be large and the native download keeps memory
 * flat.
 */
export function tenantAuditExportURL(
  tenantID: string,
  search: AuditListSearch,
  format: "csv" | "ndjson",
): string {
  const params = new URLSearchParams();
  const p = buildSearchParams(search);
  for (const [k, v] of Object.entries(p)) {
    if (v != null && v !== "") params.append(k, String(v));
  }
  params.set("format", format);
  return `${tenantBase(tenantID)}/export?${params.toString()}`;
}

export function platformAuditExportURL(
  search: AuditListSearch,
  format: "csv" | "ndjson",
): string {
  const params = new URLSearchParams();
  const p = buildSearchParams(search);
  for (const [k, v] of Object.entries(p)) {
    if (v != null && v !== "") params.append(k, String(v));
  }
  params.set("format", format);
  return `/api/v1/audit-events/export?${params.toString()}`;
}
